import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import { DialogContext } from '../../../../store/context/DialogContext';
import useRequiredDocuments, { DOC_STATUS, splitByPhase } from '../../../../hooks/useRequiredDocuments';
import useExtractionGate, { PARSE_STATE } from '../../../../hooks/useExtractionGate';
import useDocumentParseProgress from '../../../../hooks/useDocumentParseProgress';
import useExtractionStatus from '../../../../hooks/useExtractionStatus';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import DocumentRequirementList from './DocumentRequirementList';
import LockedBanner from './LockedBanner';
import DocumentFirstStepHead from './components/DocumentFirstStepHead';
import { getLockState } from './applicationLock';
import { V2_TYPE, getNextStepV2, WIZARD, WIZARD_NUM } from './helperV2';
import { VARIANT, variantClass } from '../../../../util/variant';
import { useDocTypes } from '../../../../util/docTypeRuleMeta';
import './documentsVersion2.css';

const STANDING_NOTES = [
    'Register of directors and shareholders must be certified by an authorised signatory and dated.',
    'Please review the instructions above each upload box for the specific requirements of each document.',
];

const SHOW_INDIVIDUAL_DOC_GROUPS = false;

const DocumentsVersion2 = ({
    loading,
    asLabel,
    data,
    handleUpdate,
    handlePrev,
    editId,
    applicationForm,
    wizard,
    hideButtons,
    pageNum = WIZARD_NUM.Documents,
    stepCount,
    stepLabel = 'Documents',
    v2Type = V2_TYPE.PLUS,
    jsonConfig,
    onRefetchApplication,
    onPollApplication,
    onGateChange,
    variant = VARIANT.V2,
}) => {
    const { showError } = useContext(DialogContext);
    const [internalLoading, setInternalLoading] = useState(false);
    const [uploadEpoch, setUploadEpoch] = useState(0);

    const individuals = useMemo(
        () => (Array.isArray(data?.individuals) ? data.individuals : []),
        [data?.individuals],
    );

    const lock = useMemo(
        () => getLockState(applicationForm, jsonConfig),
        [applicationForm, jsonConfig],
    );

    const { docTypeMap } = useDocTypes();

    const {
        business,
        individuals: individualGroups,
        startRequirements,
        missingStart,
        allStartUploaded,
        allStartRead,
        hasStartRequirements,
        loadedOnce,
        error: requirementsError,
        refetch,
    } = useRequiredDocuments(editId, { individuals });

    // Re-derive the extraction result from the persisted records so the gate opens
    // after a page reload — the live parse state lives only on the socket, so on a
    // fresh load there is nothing to re-open the gate and Continue stays disabled.
    // Also acts as a socket fallback via refetchExtractionStatus() in handlePoll.
    const { fetchedParseState, refetchExtractionStatus } = useExtractionStatus(editId, {
        refreshKey: uploadEpoch,
    });

    const handleDocumentsChanged = useCallback(() => {
        setUploadEpoch((n) => n + 1);
        refetch();
    }, [refetch]);

    const handlePoll = useCallback(() => {
        refetch();
        refetchExtractionStatus?.();
        onPollApplication?.();
    }, [refetch, refetchExtractionStatus, onPollApplication]);

    const handleTerminal = useCallback((payload) => {
        refetch();
        if (payload?.state === 'completed') {
            onRefetchApplication?.({ runKey: payload?.eventId || payload?.at || null });
        }
    }, [refetch, onRefetchApplication]);

    const {
        state: parseState,
        progress,
    } = useDocumentParseProgress(editId, {
        onTerminal: handleTerminal,
        uploadEpoch,
    });

    const {
        gateOpen, openedOnRead, waitingForUploads, waitingForParse, slow,
    } = useExtractionGate({
        applicationForm,
        allStartUploaded,
        allStartRead,
        hasStartRequirements,
        requirementsKnown: loadedOnce && !requirementsError,
        onPoll: handlePoll,
        // Persisted extraction result (fetchedParseState) is authoritative and wins over
        // the live socket state: the socket can report a stale "still reading" progress
        // (or nothing after a reload) even though the latest run already finished, which
        // would otherwise keep the gate closed and Continue disabled. fetchedParseState is
        // only ever a terminal value, so when it is null we fall back to the live socket.
        parseState: fetchedParseState || parseState,
    });

    // The gate opens from the persisted/REST fallback (fetchedParseState) whenever the
    // socket never delivered a 'completed' terminal — the event was missed, arrived
    // before this step mounted, or realtime was unavailable. In that case handleTerminal
    // never ran, so the extracted data was never adopted into the form. Adopt it here too,
    // mirroring the socket path; without this the next step renders empty fields and the
    // step-change autosave writes those empties back over the extraction. Keyed per
    // completion transition so a re-extraction run is adopted again.
    const adoptedPersistedRef = useRef({ last: null, seq: 0 });
    useEffect(() => {
        const state = adoptedPersistedRef.current;
        if (fetchedParseState === PARSE_STATE.COMPLETED && state.last !== PARSE_STATE.COMPLETED) {
            state.seq += 1;
            onRefetchApplication?.({ runKey: `persisted:${editId}:${state.seq}` });
        }
        state.last = fetchedParseState;
    }, [fetchedParseState, editId, onRefetchApplication]);

    useEffect(() => {
        onGateChange?.({ open: gateOpen, wizard });
    }, [gateOpen, onGateChange, wizard]);

    const handleSubmit = async () => {
        if (!gateOpen) {
            showError(
                waitingForUploads
                    ? 'Please upload the remaining documents before continuing.'
                    : 'Your documents are still being read. This step opens as soon as that finishes.',
            );
            return;
        }
        setInternalLoading(true);
        // Race guard: if extraction has finished, make sure its data is in the form state
        // before leaving — the proactive adopt above may still be in flight if Continue is
        // clicked the instant the gate opens, and navigating first would autosave empty
        // fields over the extraction. Calling without a runKey always adopts the latest.
        if (onRefetchApplication
            && (fetchedParseState === PARSE_STATE.COMPLETED || parseState === PARSE_STATE.COMPLETED)) {
            try {
                await onRefetchApplication();
            } catch (e) {
                // Non-fatal: fall through and let handleUpdate flush the current state.
            }
        }
        const nextStep = getNextStepV2(wizard, v2Type, data, jsonConfig);
        const isFinalDocumentStep = nextStep?.wizard === WIZARD.ThankYou;
        await handleUpdate({
            nextStep,
            isLastStep: isFinalDocumentStep,
            isfetchDetail: isFinalDocumentStep,
            skipUpdate: true,
        });
        setInternalLoading(false);
    };

    const isReady = loadedOnce;
    const businessByPhase = useMemo(() => splitByPhase(business), [business]);
    const startUploadsHere = businessByPhase.start.length
        + (SHOW_INDIVIDUAL_DOC_GROUPS
            ? individualGroups.reduce((n, g) => n + splitByPhase(g.documents).start.length, 0)
            : 0);
    const noStartUploads = isReady && !requirementsError && startUploadsHere === 0;
    const hasLaterDocuments = businessByPhase.final.length > 0
        || (SHOW_INDIVIDUAL_DOC_GROUPS
            ? individualGroups.some((g) => splitByPhase(g.documents).final.length > 0)
            : false);

    const pendingReason = waitingForUploads
        ? `${missingStart.length} of ${startRequirements.length} documents still needed.`
        : 'Your documents are being read. This step opens as soon as that finishes.';

    return (
        <div
            className={`docfirst ${variantClass(variant)}`.trim()}
            data-testid="business-v2-documents-first-step"
        >
            {}
            <DocumentFirstStepHead
                stepIndex={pageNum}
                stepCount={stepCount}
                stepLabel={stepLabel}
                title={waitingForParse ? 'Reading your documents' : 'Upload the documents'}
                intro={'Please upload all of the required documents for your application. If you do not have them ready, you can return later to complete the application.'}
                dataTestId="business-v2-documents-first-step-head"
            />
            <LockedBanner show={lock.readOnlyAll} variant={variant} />

            {!isReady ? (
                <div className="docfirst__loading">
                    <span className="text-muted">Fetching document requirements…</span>
                </div>
            ) : (
                <div className="docfirst__card">
                    <div className="docfirst__panel">
                        <div>
                            <div className="docfirst__panel-title">Important things to note:</div>
                            <ul className="docfirst__panel-list">
                                {STANDING_NOTES.map((note) => (
                                    <li key={note}>{note}</li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    {waitingForParse && (
                        <Card className="docfirst__gate p-3 mb-3">
                            {}
                            {progress?.total ? (
                                <div className="docfirst__gate-head">
                                    <span className="fw-semibold">
                                        {`Reading ${progress.total} document${progress.total === 1 ? '' : 's'}`}
                                    </span>
                                    {progress.done > 0 ? (
                                        <span className="docfirst__gate-count">
                                            {`${progress.done} of ${progress.total} done`}
                                        </span>
                                    ) : null}
                                </div>
                            ) : null}
                            <div className="docfirst__progress" aria-hidden="true">
                                <div className="docfirst__progress-bar" />
                            </div>
                            {}
                            {slow ? (
                                <div className="text-muted small mt-2">
                                    This is taking longer than expected. Your documents are stored and the read
                                    continues without you, so it is safe to leave and come back. Any fields it
                                    would have filled can simply be typed in.
                                </div>
                            ) : (
                                <div className="docfirst__panel mt-3 mb-0">
                                    <div>
                                        <div className="docfirst__panel-title">You do not need to wait here</div>
                                        <div className="docfirst__panel-body">
                                            Save and exit, and reopen the application later — it will be waiting
                                            at the next step. Nothing is lost by leaving.
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Card>
                    )}

                    {requirementsError && (
                        <Card className="docfirst__failed p-3 mb-3">
                            <div className="fw-semibold">We could not load the document list</div>
                            <div className="text-muted small">This is usually temporary.</div>
                            <div className="mt-2">
                                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={refetch}>
                                    Try again
                                </button>
                            </div>
                        </Card>
                    )}

                    {noStartUploads && (
                        <Card className="p-3 mb-3">
                            <div className="fw-semibold">
                                {hasLaterDocuments
                                    ? 'No documents to upload at this step.'
                                    : 'No documents required at this stage.'}
                            </div>
                            <div className="text-muted small">
                                {hasLaterDocuments
                                    ? 'You can continue — the remaining documents are requested at a later step.'
                                    : 'We will notify you if additional verification materials are required later.'}
                            </div>
                        </Card>
                    )}

                    {}
                    {SHOW_INDIVIDUAL_DOC_GROUPS && individualGroups.map((group) => {
                        const phases = splitByPhase(group.documents);
                        return (
                            <div className="docfirst__group" key={group.ownerId}>
                                <div className="docfirst__group-head">
                                    <span className="docfirst__group-name">{group.displayName}</span>
                                    <span className="docfirst__group-count">
                                        {group.documents.filter((d) => d.status === DOC_STATUS.UPLOADED).length}
                                        {' of '}
                                        {group.documents.length}
                                    </span>
                                </div>
                                <DocumentRequirementList
                                    documents={phases.start}
                                    recordId={editId}
                                    asLabel={asLabel}
                                    docTypeMap={docTypeMap}
                                    onDocumentsChanged={handleDocumentsChanged}
                                />
                                {}
                                {phases.final.length > 0 && (
                                    <>
                                        <div className="docfirst__group-subhead">Needed once the people are added</div>
                                        <DocumentRequirementList
                                            documents={phases.final}
                                            recordId={editId}
                                            asLabel={asLabel}
                                            docTypeMap={docTypeMap}
                                            onDocumentsChanged={handleDocumentsChanged}
                                        />
                                    </>
                                )}
                            </div>
                        );
                    })}

                    {businessByPhase.start.length > 0 && (
                        <div className="docfirst__group">
                            {SHOW_INDIVIDUAL_DOC_GROUPS && individualGroups.length > 0 && (
                                <div className="docfirst__group-head">
                                    <span className="docfirst__group-name">The business</span>
                                </div>
                            )}
                            {}
                            <DocumentRequirementList
                                documents={businessByPhase.start}
                                recordId={editId}
                                asLabel={asLabel}
                                docTypeMap={docTypeMap}
                                onDocumentsChanged={handleDocumentsChanged}
                            />
                        </div>
                    )}

                    {}
                    {startRequirements.length > 0 && (
                        <div className="docfirst__panel mt-3">
                            <div>
                                <div className="docfirst__panel-title">
                                    More will be asked once these are read
                                </div>
                                <div className="docfirst__panel-body">
                                    Proof of address, the structure chart, and identity documents for each owner
                                    we find. We ask for those later so you are not gathering paperwork for
                                    people we have not identified yet.
                                </div>
                            </div>
                        </div>
                    )}

                    {}
                    {openedOnRead && (
                        <div className="docfirst__panel-body mt-3">
                            Your documents have been read. We may still be filling some details in
                            for you, so check the next steps before you submit.
                        </div>
                    )}

                    {waitingForParse && (
                        <div className="docfirst__progress mt-3" aria-hidden="true">
                            <div className="docfirst__progress-bar" />
                        </div>
                    )}

                    <div className="mt-4 business-v2-step-actions">
                        <ButtonGroupNew
                            onNextClick={handleSubmit}
                            onBackClick={handlePrev}
                            nextText="Continue"
                            showPageNum={false}
                            pageNum={pageNum}
                            step={pageNum}
                            isDisabled={asLabel || !gateOpen}
                            isReadyForReviewAction={gateOpen}
                            reviewPendingReason={gateOpen ? '' : pendingReason}
                            showPendingReasonInline
                            hideSubmitButton={hideButtons || Boolean(asLabel)}
                            loading={internalLoading || loading}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentsVersion2;

